import React, { useEffect, useRef, useState } from 'react';
import SatValidationErrMsg from '@components/common/satUI/SatValidationErrMsg';
import SatQuestionArea from '@components/common/satUI/SatQuestionArea';
import SatCheckBox from '@components/common/satUI/SatCheckBox';
import SatSignPad from '@components/common/satUI/SatSignPad';
import SatButton from '@components/common/satUI/SatButton';
import SatInput from '@components/common/satUI/SatInput';
import { changeEaState } from '@store/actions/eaAction';
import { showModal } from '@store/actions/commonAction';
import { useDispatch } from 'react-redux';
import { EA_PDF_STEP_INFO } from '@common/constants';

const CompletePurchaseDocument = ({
  onSave,
  onClickPrev,
  contractSign,
  isVariable,
  ownerName,
  isPracticeMode,
  signData,
  signPasteData,
  searchPasteSignData,
  searchSignData,
  practiceSignName,
  practiceSignature,
  complete,
}) => {
  const dispatch = useDispatch();

  const completePurchase = contractSign.find(
    info => info.step === EA_PDF_STEP_INFO.completePurchase,
  )?.completePurchase;

  const [isValid, setIsValid] = useState(false); // 확인버튼 disabled
  const [isCheckError, setIsCheckError] = useState(false); // 에러버튼
  const [questionCheckValue, setQuestionCheckValue] = useState([]);
  const [showErrorCheckMsg, setShowErrorCheckMsg] = useState(false);
  const [pasteCheck, setPasteCheck] = useState(false); // 붙여넣기 체크 여부
  const [signNameData, setSignNameData] = useState(''); // 성명
  const [signatureData, setSignatureData] = useState(''); // 서명

  const mainPurposeInputRef = useRef();
  const canvasSignNameRef = useRef();
  const canvasSignatureRef = useRef();

  // 완료된 페이지의 경우 기존 저장된 서명데이터 조회
  useEffect(() => {
    if (
      complete &&
      !isPracticeMode &&
      (!signData.app_form_etc_51_signid_01 ||
        !signData.app_form_etc_51_signid_02)
    ) {
      searchSignData(
        'app_form_etc_51',
        'app_form_etc_51_signid_01',
        'app_form_etc_51_signid_02',
      );
    }
  }, [complete]);

  // 버튼 상태값 변경
  useEffect(() => {
    if (completePurchase) {
      if (completePurchase.mainPurpose.trim() === '') return setIsValid(false); // 주목적
      if (completePurchase.check_yn_1 !== 'Y') return setIsValid(false); // 자필서명여부Yn
      if (completePurchase.check_yn_2 !== 'Y') return setIsValid(false); // 가입제안서전달여부Yn
      if (completePurchase.check_yn_3 !== 'Y') return setIsValid(false); // 주요내용성명여부Yn
      if (isVariable && completePurchase.check_yn_4 !== 'Y')
        return setIsValid(false); // 적합성자필서명Yn
      if (completePurchase.check_yn_5 === '') return setIsValid(false); // 변동권유경험
      if (completePurchase.check_yn_5 === 'Y') {
        if (completePurchase.confirm_1 !== 'Y') return setIsValid(false); // 원금손실가능성안내
      }
      if (completePurchase.check_yn_6 !== 'Y') return setIsValid(false); // 선납/추가납입 원금손실가능성안냉부Yn
      if (completePurchase.check_yn_7 !== 'N') return setIsValid(false); // 미승인자료 상품설명여부Yn
      if (completePurchase.check_yn_8 !== 'N') return setIsValid(false); // 제3자명의계약권유 금전적대가 여부Yn
      if (signNameData === '' || signatureData === '') return setIsValid(false);
      return setIsValid(true);
    }
  }, [completePurchase, signNameData, signatureData]);

  // 예, 아니오 버튼
  const handleSaveData = (type, value) => {
    if (type !== 'mainPurpose' && type !== 'check_yn_5') {
      if (
        ((type === 'check_yn_7' || type === 'check_yn_8') && value === 'Y') ||
        (type !== 'check_yn_7' && type !== 'check_yn_8' && value === 'N')
      ) {
        dispatch(
          showModal({
            visible: true,
            type: 'info',
            msg: '선택하신 항목으로는 청약을 진행할 수 없습니다.',
          }),
        );
        setIsValid(false);
      }
    }

    let changeData;
    changeData = {
      ...completePurchase,
      [type]: value,
    };

    const newContractSign = contractSign.map(info => {
      if (info.step === EA_PDF_STEP_INFO.completePurchase)
        return {
          ...info,
          completePurchase: { ...info.completePurchase, ...changeData },
        };
      else return info;
    });
    const requestData = { type: 'contractSign', data: newContractSign };
    dispatch(changeEaState(requestData));
  };

  // 확인버튼 클릭
  const handleButtonClick = () => {
    if (isValid) {
      onSave({
        saveData: completePurchase,
        signNameData: signNameData,
        signatureData: signatureData,
        reportId: 'app_form_etc_51',
        nameId: 'app_form_etc_51_signid_01',
        signId: 'app_form_etc_51_signid_02',
      });
    } else {
      if (completePurchase.mainPurpose === '') {
        mainPurposeInputRef.current.onFocus();
        setIsCheckError(true);
      }
    }
  };

  // 붙여넣기 서명 조회
  const handlePasteCheck = value => {
    if (value && !isPracticeMode) {
      searchPasteSignData();
    }
    setPasteCheck(value);
  };

  return (
    <>
      <header className="con-header">
        <div className="page-navi">
          <SatButton
            styleClass="btn-back"
            label="이전"
            onClick={() => onClickPrev()}
          />
        </div>
        <h1 className="page-tit">완전 판매 확인서</h1>
      </header>

      <div className="con-wrap">
        <div className="pop-box">
          <div className="infor-list pdb-20">
            <h2 className="tit-con">완전판매 확인사항</h2>
            <div className="txt-question">
              <p>가입하신 보험상품의 종류 및 주목적은 무엇입니까?</p>
            </div>
            <div className="box mgt-8">
              <SatInput
                ref={mainPurposeInputRef}
                value={completePurchase?.mainPurpose}
                type="text"
                label="직접입력"
                maxLength={30}
                onChange={value => handleSaveData('mainPurpose', value)}
              />
            </div>
            <SatValidationErrMsg
              visible={isCheckError && completePurchase.mainPurpose === ''}
              type="error"
              message="내용을 확인하고 입력해주세요."
            />
            <SatQuestionArea
              title={() => (
                <p>
                  청약서 및 상품설명서 등 제반 가입서류에 자필서명을 직접
                  하셨습니까?
                </p>
              )}
              selectValue={completePurchase?.check_yn_1}
              options={[
                { label: '예', value: 'Y' },
                { label: '아니요', value: 'N' },
              ]}
              useType={'btn'}
              name={'check_yn_1'}
              onClick={value => handleSaveData('check_yn_1', value)}
              error={''}
            />

            <SatQuestionArea
              title={() => (
                <p>
                  계약자보관용 청약서 및 보험 약관, 가입제안서는 전달
                  받았습니까?
                </p>
              )}
              selectValue={completePurchase?.check_yn_2}
              options={[
                { label: '예', value: 'Y' },
                { label: '아니요', value: 'N' },
              ]}
              useType={'btn'}
              name={'check_yn_2'}
              onClick={value => handleSaveData('check_yn_2', value)}
              error={''}
            />

            <SatQuestionArea
              title={() => (
                <p>
                  상품의 주요내용을 정확하게 설명 받고, 설명서를 제공
                  받으셨습니까?
                </p>
              )}
              selectValue={completePurchase?.check_yn_3}
              options={[
                { label: '예', value: 'Y' },
                { label: '아니요', value: 'N' },
              ]}
              useType={'btn'}
              name={'check_yn_3'}
              onClick={value => handleSaveData('check_yn_3', value)}
              error={''}
            />

            {isVariable && (
              <SatQuestionArea
                title={() => (
                  <p>
                    (변액보험) 적합성, 적정성 진단을 받으시고 자필서명을 직접
                    하셨습니까?
                  </p>
                )}
                selectValue={completePurchase?.check_yn_4}
                options={[
                  { label: '예', value: 'Y' },
                  { label: '아니요', value: 'N' },
                ]}
                useType={'btn'}
                name={'check_yn_4'}
                onClick={value => handleSaveData('check_yn_4', value)}
                error={''}
              />
            )}

            <div className="mgt-0">
              <p className="text-info ico-type">
                상기 질문 中 ‘아니요‘ 답변이 있으시거나, 본인의사와 다른 서명을
                요구하는 경우 청약 중단 및 계약취소가 가능합니다.
              </p>
            </div>
          </div>

          <div className="infor-list no-line">
            <h2 className="tit-con">모집설계사 상품설명 확인</h2>
            <p className="text-medium bold">부당권유행위 확인</p>
            <SatQuestionArea
              title={() => (
                <p>
                  금번 보험상품을 새롭게 가입하면서 담당 설계사로부터 기존계약에
                  대해 향후 ‘해약이나 해지, 실효, 계좌변경, 보험료 납입 중지,
                  중도인출, 약관대출‘ 등의 계약 변동 권유를 받으신 적이
                  있습니까?
                </p>
              )}
              selectValue={completePurchase?.check_yn_5}
              options={[
                { label: '①있다', value: 'Y' },
                { label: '②없다', value: 'N' },
              ]}
              useType={'btn'}
              name={'check_yn_5'}
              onClick={value => handleSaveData('check_yn_5', value)}
              error={''}
            />

            {completePurchase?.check_yn_5 === 'Y' && (
              <SatQuestionArea
                title={() => (
                  <p className="under">
                    ‘①있다’의 경우 반드시 원금손실 가능성 안내를 받으셔야
                    합니다. 받으셨습니까?
                    <br />★ 기존계약을 해약하고 신계약으로 보험계약이 이전하는
                    (옮겨지는) 것은 불가능합니다.
                  </p>
                )}
                selectValue={completePurchase?.confirm_1}
                options={[
                  { label: '예', value: 'Y' },
                  { label: '아니요', value: 'N' },
                ]}
                useType={'btn'}
                name={'confirm_1'}
                onClick={value => handleSaveData('confirm_1', value)}
                error={''}
              />
            )}

            <p className="text-medium bold mgt-40">과장광고행위 확인</p>
            <SatQuestionArea
              title={() => (
                <p>
                  금번 보험상품을 새롭게 가입하면서{' '}
                  <span className="blue under">선납/추가납입</span>을 진행할
                  경우 아래의 해당내용을 정확히 확인하셨습니까? (선납,
                  추가납입은 고객님의 불가피한 사정으로 납입이 진행되지 않을
                  경우가 발생함)
                  <br />
                  <span className="under">
                    - 선납 해당 기간 이후 납입이 진행되지 않을 경우 실효 및 그에
                    따른 원금 손실 가능성에 대한 안내를 받으셨습니까?
                  </span>
                </p>
              )}
              selectValue={completePurchase?.check_yn_6}
              options={[
                { label: '예', value: 'Y' },
                { label: '아니요', value: 'N' },
              ]}
              useType={'btn'}
              name={'check_yn_6'}
              onClick={value => handleSaveData('check_yn_6', value)}
              error={''}
            />

            <SatQuestionArea
              title={() => (
                <p>
                  금번 보험상품을 새롭게 가입하면서 담당 설계사로부터 회사의
                  승인된 상품설명자료가 아닌 미승인 자료로 상품설명을 받으신
                  적이 있습니까?
                </p>
              )}
              selectValue={completePurchase?.check_yn_7}
              options={[
                { label: '있다', value: 'Y' },
                { label: '없다', value: 'N' },
              ]}
              useType={'btn'}
              name={'check_yn_7'}
              onClick={value => handleSaveData('check_yn_7', value)}
              error={''}
            />

            <p className="text-medium bold mgt-40">금지행위 확인</p>
            <SatQuestionArea
              title={() => (
                <p>
                  금번 보험상품을 새롭게 가입하면서 설계사 본인이 아닌 제3자의
                  명의로 계약을 권유 받으시거나, 수수료·보수 등 금전적 대가를
                  받으신 적이 있습니까?
                </p>
              )}
              selectValue={completePurchase?.check_yn_8}
              options={[
                { label: '있다', value: 'Y' },
                { label: '없다', value: 'N' },
              ]}
              useType={'btn'}
              name={'check_yn_8'}
              onClick={value => handleSaveData('check_yn_8', value)}
              error={''}
            />
          </div>
          <div className="info-box bg-yellow">
            <p className="text-info3">
              상기 보험계약의 확인사항에 대해 정확하게 충분히 상담 받고,
              숙지하여 기재하였으며f, 계약자 본인이 직접 자필서명 완료하였음을
              확인합니다.
            </p>
          </div>
          <SatCheckBox
            value={[pasteCheck]}
            type={'radio'}
            options={[
              { label: '저장했던 성명/서명을 붙여넣으세요.', value: false },
            ]}
            onChange={v => handlePasteCheck(v)}
            customStyleClass="small w-360"
          />
          <div className="sign-pad-wrap" id="signDataWrap">
            <SatSignPad
              canvasRef={canvasSignNameRef}
              name={ownerName}
              title={'계약자 성명 입력'}
              error={
                showErrorCheckMsg && !signNameData ? '성명을 입력해주세요.' : ''
              }
              pasteCheck={pasteCheck}
              pasteData={
                !isPracticeMode
                  ? signPasteData.app_form_etc_18_sign_01
                  : practiceSignName
              }
              signData={complete ? signData.app_form_etc_51_signid_01 : ''}
              onChange={value => setSignNameData(value)}
              onChangePasteCheck={value => setPasteCheck(value)}
            />
            <SatSignPad
              canvasRef={canvasSignatureRef}
              name={'서명'}
              title={'계약자 서명 입력'}
              error={
                showErrorCheckMsg && !signatureData
                  ? '서명을 입력해주세요.'
                  : ''
              }
              pasteCheck={pasteCheck}
              pasteData={
                !isPracticeMode
                  ? signPasteData.app_form_etc_18_sign_02
                  : practiceSignature
              }
              signData={complete ? signData.app_form_etc_51_signid_02 : ''}
              onChange={value => setSignatureData(value)}
              onChangePasteCheck={value => setPasteCheck(value)}
            />
          </div>
        </div>
      </div>
      <div className="con-bottom box-shadow">
        <div className="btn-area">
          <div className="box-btns-center">
            <SatButton
              styleClass={`btn-s blue btn-wide ${!isValid ? 'disabled' : ''}`}
              label="계약자 서명 및 입력 저장"
              onClick={() => handleButtonClick()}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CompletePurchaseDocument;
